// Submit a CUSTOM uploaded photo (not a try-on) to admin review for Featured.
// Costs 5 credits, or 1 "custom-photo Featured pass" earned at the 14/30-day
// daily check-in milestones. Same caption + consent rules as try-on submission.
import { useEffect, useRef, useState } from "react";
import { Sparkles, Loader2, Coins, Instagram, ShieldCheck, Camera, Upload, Crown } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/contexts/AuthContext";
import { useCredits } from "@/hooks/useCredits";
import { useUserPerks } from "@/hooks/useUserPerks";
import { compressImage, uploadToBucket } from "@/lib/storage";
import {
  validateFeaturedCaption,
  captionErrorMessage,
  normalizeInstagramHandle,
} from "@/lib/featured-caption";

interface Props {
  open: boolean;
  onClose: () => void;
}

const COST = 5;
const ROW = "flex items-start gap-2.5 text-[12.5px] leading-snug";

export const SubmitCustomFeaturedSheet = ({ open, onClose }: Props) => {
  const { user } = useAuth();
  const { credits, refresh: refreshCredits } = useCredits();
  const { wallet, refresh: refreshPerks } = useUserPerks();
  const fileRef = useRef<HTMLInputElement>(null);

  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploadedPath, setUploadedPath] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [caption, setCaption] = useState("");
  const [ig, setIg] = useState("");
  const [showIg, setShowIg] = useState(true);
  const [contentRights, setContentRights] = useState(false);
  const [publicDisplay, setPublicDisplay] = useState(false);
  const [igOutreach, setIgOutreach] = useState(false);
  const [ageOk, setAgeOk] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setPreviewUrl((u) => { if (u) URL.revokeObjectURL(u); return null; });
      setUploadedPath(null);
      setCaption("");
      setIg("");
      setShowIg(true);
      setContentRights(false);
      setPublicDisplay(false);
      setIgOutreach(false);
      setAgeOk(false);
    }
  }, [open]);

  const captionCheck = validateFeaturedCaption(caption);
  const igTrimmed = ig.trim();
  const igNormalized = igTrimmed ? normalizeInstagramHandle(igTrimmed) : null;
  const igInvalid = !!igTrimmed && !igNormalized;

  const hasPass = wallet.customFeaturedPasses > 0;
  const hasEnoughCredits = (credits ?? 0) >= COST;
  const canPay = hasPass || hasEnoughCredits;

  const requiredConsents = contentRights && publicDisplay && ageOk;
  const igConsentOk = !igNormalized || igOutreach;
  const canSubmit =
    !submitting && !uploading && uploadedPath !== null &&
    canPay && captionCheck.ok && !igInvalid && requiredConsents && igConsentOk;

  const onPickFile = async (f: File | null) => {
    if (!f || !user) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Please pick an image file.");
      return;
    }
    setUploading(true);
    try {
      const compressed = await compressImage(f, 1.5, 1600);
      const path = await uploadToBucket("user-photos", user.id, compressed, "jpg");
      setUploadedPath(path);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(URL.createObjectURL(compressed));
    } catch (e) {
      console.error(e);
      toast.error("Couldn't upload that photo. Try another.");
    } finally {
      setUploading(false);
    }
  };

  const onSubmit = async () => {
    if (!uploadedPath || !canSubmit) return;
    setSubmitting(true);
    const { data, error } = await supabase.functions.invoke("submit-featured-custom", {
      body: {
        photo_path: uploadedPath,
        caption: caption.trim(),
        instagram_handle: igNormalized,
        show_instagram: showIg,
        consents: {
          content_rights: contentRights,
          public_display: publicDisplay,
          instagram_outreach: igOutreach,
          age_confirmed: ageOk,
        },
      },
    });
    setSubmitting(false);
    // FunctionsHttpError puts the response in error.context; also handle data.error
    let code = "";
    if (data?.error) code = String(data.error);
    if (!code && error && (error as { context?: Response }).context) {
      try {
        const body = await (error as { context: Response }).context.clone().json();
        code = String(body?.error ?? "");
      } catch { /* ignore */ }
    }
    if (error || code) {
      if (code === "insufficient_credits") toast.error(`You need ${COST} credits, or earn a free pass with a 14-day streak.`);
      else if (code === "rate_limited") toast.error("Free tier allows 1 custom photo per 24h. Buy credits to submit unlimited.");
      else if (code === "consent_required") toast.error("Please accept the required consents.");
      else if (code === "instagram_consent_required") toast.error("Please confirm Instagram outreach consent.");
      else if (code === "bad_instagram_handle") toast.error("That Instagram handle looks invalid.");
      else if (code === "photo_not_found") toast.error("Photo upload didn't complete. Try uploading again.");
      else if (code.startsWith("caption_")) toast.error("Caption must be ≤3 words or ≤3 hashtags.");
      else toast.error(code ? `Couldn't submit (${code})` : "Couldn't submit. Try again.");
      return;
    }
    await Promise.all([refreshCredits(), refreshPerks()]);
    onClose();
    toast.success("Submitted for review", {
      description: "Within ~24h an admin will review. If approved, it appears in the Featured feed.",
      duration: 6000,
    });
  };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="bottom" className="rounded-t-3xl border-t border-white/10 max-h-[92vh] overflow-y-auto">
        <SheetHeader className="text-left">
          <SheetTitle className="flex items-center gap-2">
            <Camera className="h-5 w-5 text-primary" /> Custom photo to Featured
          </SheetTitle>
          <SheetDescription>
            Upload any photo of yourself or your outfit. Admin reviews within 24 hours.
            {hasPass ? (
              <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-orange-500/20 px-2 py-0.5 text-[10px] font-semibold text-orange-600 dark:text-orange-300">
                <Crown className="h-3 w-3" /> Free pass available
              </span>
            ) : null}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4 space-y-4">
          {/* Photo picker */}
          <div className="space-y-1.5">
            <label className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Photo</label>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => onPickFile(e.target.files?.[0] ?? null)}
            />
            {previewUrl ? (
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="relative aspect-[3/4] w-full overflow-hidden rounded-2xl ring-1 ring-white/10"
              >
                <img src={previewUrl} alt="Preview" className="h-full w-full object-cover" />
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-3 py-2 text-left text-[11px] font-semibold text-white">
                  Tap to replace
                </div>
              </button>
            ) : (
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="flex w-full flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-white/15 bg-muted/30 py-10 text-sm text-muted-foreground disabled:opacity-60"
              >
                {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Upload className="h-5 w-5" />}
                <span>{uploading ? "Uploading…" : "Tap to upload a photo"}</span>
              </button>
            )}
          </div>

          {/* Caption */}
          <div className="space-y-1.5">
            <label className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Caption (optional) — 3 words OR 3 #tags
            </label>
            <Input
              value={caption}
              onChange={(e) => setCaption(e.target.value.slice(0, 60))}
              placeholder="#streetwear #monochrome #fit"
              maxLength={60}
            />
            <div className="flex items-center justify-between text-[10px]">
              <span className={captionCheck.ok ? "text-muted-foreground" : "text-destructive"}>
                {captionCheck.ok ? "Looks great." : captionErrorMessage(captionCheck.reason)}
              </span>
              <span className="text-muted-foreground">{caption.length}/60</span>
            </div>
          </div>

          {/* Instagram */}
          <div className="space-y-1.5">
            <label className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Instagram handle (optional)
            </label>
            <div className="relative">
              <Instagram className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={ig}
                onChange={(e) => setIg(e.target.value.slice(0, 60))}
                placeholder="@yourhandle"
                className="pl-9"
              />
            </div>
            <p className="text-[10px] text-muted-foreground">
              Brands and stylists may DM you here. Leave blank to stay private.
            </p>
            {igInvalid && (
              <p className="text-[10px] text-destructive">Letters, numbers, dots, underscores only.</p>
            )}
            {igNormalized && (
              <label className="mt-1 flex items-center gap-2 text-[12px]">
                <Checkbox checked={showIg} onCheckedChange={(v) => setShowIg(!!v)} />
                <span>Show <span className="font-semibold">@{igNormalized}</span> on my featured card</span>
              </label>
            )}
          </div>

          {/* Consent */}
          <div className="rounded-2xl border border-white/10 bg-muted/30 p-3 space-y-2.5">
            <p className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-foreground">
              <ShieldCheck className="h-3.5 w-3.5 text-primary" /> Consent
            </p>
            <label className={ROW}>
              <Checkbox checked={contentRights} onCheckedChange={(v) => setContentRights(!!v)} className="mt-0.5" />
              <span>This is my photo, or I have full rights to share it.</span>
            </label>
            <label className={ROW}>
              <Checkbox checked={publicDisplay} onCheckedChange={(v) => setPublicDisplay(!!v)} className="mt-0.5" />
              <span>I agree FitAura can publicly display this image inside the app for other users.</span>
            </label>
            {igNormalized && (
              <label className={ROW}>
                <Checkbox checked={igOutreach} onCheckedChange={(v) => setIgOutreach(!!v)} className="mt-0.5" />
                <span>I agree to display my Instagram handle publicly so others can reach out.</span>
              </label>
            )}
            <label className={ROW}>
              <Checkbox checked={ageOk} onCheckedChange={(v) => setAgeOk(!!v)} className="mt-0.5" />
              <span>I am 13 years or older.</span>
            </label>
          </div>

          <div className="rounded-xl border border-white/10 bg-muted/20 p-3 text-xs text-muted-foreground">
            <p className="flex items-center gap-1.5 font-medium text-foreground">
              <Coins className="h-3.5 w-3.5 text-primary" />
              {hasPass ? `Free with your custom-photo pass` : `Cost: ${COST} credits`}
            </p>
            <p className="mt-1">Earn a free pass by checking in 14 days in a row. Admin review within ~24h.</p>
          </div>

          <Button
            className="h-12 w-full rounded-full gradient-primary text-primary-foreground disabled:opacity-60"
            disabled={!canSubmit}
            onClick={onSubmit}
          >
            {submitting ? (
              <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Submitting…</>
            ) : !uploadedPath ? (
              "Upload a photo first"
            ) : !canPay ? (
              `Need ${COST} credits or a streak pass`
            ) : !requiredConsents ? (
              "Accept consents to continue"
            ) : (
              <><Sparkles className="mr-2 h-4 w-4" /> {hasPass ? "Submit · Free pass" : `Submit · ${COST} credits`}</>
            )}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};
